import { useTranslation } from "react-i18next";
import { Wifi, Activity, Bell, Smartphone, Watch, DoorOpen, Thermometer } from "lucide-react";

export default function HowItWorks() {
  const { t } = useTranslation();

  const directMonitoring = [
    { icon: Watch, title: t("howItWorks.direct1Title"), description: t("howItWorks.direct1Desc"), highlight: true },
    { icon: Smartphone, title: t("howItWorks.direct2Title"), description: t("howItWorks.direct2Desc"), highlight: false }
  ];

  const indirectMonitoring = [
    { icon: DoorOpen, title: t("howItWorks.indirect1Title"), description: t("howItWorks.indirect1Desc"), highlight: false },
    { icon: Thermometer, title: t("howItWorks.indirect2Title"), description: t("howItWorks.indirect2Desc"), highlight: true }
  ];

  return (
    <section id="ka-tas-darbojas" className="section-spacing gradient-bg" data-testid="how-it-works-section">
      <div className="section-container">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
            {t("howItWorks.badge")}
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t("howItWorks.title")}
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground">
            {t("howItWorks.description")}
          </p>
        </div>

        {/* Main Flow */}
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-border mb-12">
          <div className="grid md:grid-cols-3 gap-8 items-center">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Wifi className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-bold text-lg mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("howItWorks.step1Title")}</h3>
              <p className="text-muted-foreground text-sm">{t("howItWorks.step1Desc")}</p>
            </div>
            <div className="text-center p-6 bg-primary/5 rounded-2xl">
              <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Activity className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-lg mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("howItWorks.step2Title")}</h3>
              <p className="text-muted-foreground text-sm">{t("howItWorks.step2Desc")}</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Bell className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-bold text-lg mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("howItWorks.step3Title")}</h3>
              <p className="text-muted-foreground text-sm">{t("howItWorks.step3Desc")}</p>
            </div>
          </div>
        </div>

        {/* Direct vs Indirect Monitoring */}
        <div className="grid lg:grid-cols-2 gap-8">
          <div>
            <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center text-white text-sm font-bold">T</span>
              {t("howItWorks.directTitle")}
            </h3>
            <div className="space-y-4">
              {directMonitoring.map((item, index) => (
                <div key={index} className={`p-6 rounded-2xl ${item.highlight ? 'bg-primary text-white' : 'bg-white border border-border'}`} data-testid={`direct-monitoring-${index + 1}`}>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${item.highlight ? 'bg-white/20' : 'bg-primary/10'}`}>
                    <item.icon className={`w-6 h-6 ${item.highlight ? 'text-white' : 'text-primary'}`} />
                  </div>
                  <h4 className={`font-bold text-lg mb-2 ${item.highlight ? 'text-white' : 'text-foreground'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{item.title}</h4>
                  <p className={item.highlight ? 'text-white/90' : 'text-muted-foreground'}>{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span className="w-10 h-10 bg-accent rounded-xl flex items-center justify-center text-white text-sm font-bold">N</span>
              {t("howItWorks.indirectTitle")}
            </h3>
            <div className="space-y-4">
              {indirectMonitoring.map((item, index) => (
                <div key={index} className={`p-6 rounded-2xl ${item.highlight ? 'bg-primary text-white' : 'bg-white border border-border'}`} data-testid={`indirect-monitoring-${index + 1}`}>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${item.highlight ? 'bg-white/20' : 'bg-primary/10'}`}>
                    <item.icon className={`w-6 h-6 ${item.highlight ? 'text-white' : 'text-primary'}`} />
                  </div>
                  <h4 className={`font-bold text-lg mb-2 ${item.highlight ? 'text-white' : 'text-foreground'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{item.title}</h4>
                  <p className={item.highlight ? 'text-white/90' : 'text-muted-foreground'}>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Banner */}
        <div className="mt-12 bg-primary/5 rounded-2xl p-8 text-center">
          <p className="text-lg text-foreground font-medium">
            <span className="text-primary font-bold">{t("howItWorks.banner")}</span> {t("howItWorks.bannerDesc")}
          </p>
        </div>
      </div>
    </section>
  );
}
